import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
// material
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { useOpenSnackbar } from "@hooks/useSnackbar";
import axios from "@utils/axios";
import { formatKstDate } from "@utils/kst";
import { adminRefreshTick } from "@states/admin";
import { MembershipGroupType } from "types/admin";
import { MembershipLevel } from "types/auth";
import { LEVEL_LABEL } from "./levels";

const LEVELS: MembershipLevel[] = ["basic", "pro", "elite", "admin"];
const FOREVER = "9999-12-31";

function errorText(error: any, fallback: string): string {
  return typeof error?.detail === "string" ? error.detail : fallback;
}

type Props = {
  /** `null` 이면 닫힘, `"new"` 면 생성, 그룹이면 수정 */
  target: MembershipGroupType | "new" | null;
  onClose: () => void;
};

export default function MembershipFormDialog({ target, onClose }: Props) {
  const bumpRefresh = useSetRecoilState(adminRefreshTick);
  const openSnackbar = useOpenSnackbar();
  const [busy, setBusy] = useState(false);
  const editing = target !== null && target !== "new" ? target : null;

  const [name, setName] = useState("");
  const [level, setLevel] = useState<MembershipLevel>("pro");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!target) return;
    if (target === "new") {
      setName("");
      setLevel("pro");
      // `toISOString()` 은 **UTC** 다. 한국 새벽 0~9시에 이 창을 열면 어제 날짜가
      // 기본값이 되고, 그대로 저장하면 멤버십이 **하루 일찍 시작한다.** 유효 기간
      // 판정은 서버가 한국 날짜(`today_kst`)로 하므로 기본값도 한국 날짜여야 한다.
      setStartDate(formatKstDate(new Date().toISOString()));
      setEndDate(FOREVER);
    } else {
      setName(target.name);
      setLevel((target.level ?? "pro") as MembershipLevel);
      setStartDate(target.startDate);
      setEndDate(target.endDate);
    }
  }, [target]);

  const submit = async () => {
    setBusy(true);
    const body = { name, level, startDate, endDate };
    try {
      if (editing) {
        await axios.patch(`memberships/${editing.id}`, body);
        openSnackbar(`${name} 을(를) 수정했습니다.`, "success");
      } else {
        await axios.post("memberships", body);
        openSnackbar(`${name} 을(를) 만들었습니다.`, "success");
      }
      bumpRefresh((tick) => tick + 1);
      onClose();
    } catch (error: any) {
      openSnackbar(errorText(error, "처리하지 못했습니다. 잠시 후 다시 시도해주세요."), "error");
      bumpRefresh((tick) => tick + 1);
    } finally {
      setBusy(false);
    }
  };

  const invalidRange = Boolean(startDate && endDate && startDate > endDate);

  return (
    <Dialog open={Boolean(target)} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{editing ? "멤버십 그룹 수정" : "새 멤버십 그룹"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* 수정은 그 그룹에 속한 전원에게 한꺼번에 적용된다. 인원을 숫자로 보여 준다. */}
          {editing && editing.userCount > 0 && (
            <Alert severity="warning">
              이 변경은 <b>{editing.userCount}명</b>에게 적용됩니다. 등급을 내리거나 기간을
              과거로 옮기면 그만큼의 사람이 한꺼번에 권한을 잃습니다.
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            size="small"
            label="그룹 이름"
            value={name}
            inputProps={{ maxLength: 36 }}
            onChange={(event) => setName(event.target.value)}
            helperText="같은 이름은 쓸 수 없습니다. 기수는 이름에 붙입니다 (예: 투자캠퍼스 2기)"
          />

          <TextField
            select
            fullWidth
            size="small"
            label="등급"
            value={level}
            onChange={(event) => setLevel(event.target.value as MembershipLevel)}
          >
            {LEVELS.map((value) => (
              <MenuItem key={value} value={value}>
                {LEVEL_LABEL[value].text}
              </MenuItem>
            ))}
          </TextField>

          {/* 관리자를 만들 수 있는 그릇이 하나 느는 것이다. 막지는 않는다. */}
          {level === "admin" && (
            <Alert severity="warning">
              <b>관리자 등급</b>입니다. 이 그룹에 넣는 사람은 관리 화면 전체를 쓰게 됩니다.
            </Alert>
          )}

          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="시작일"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="종료일"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              helperText={endDate === FOREVER ? "무기한" : " "}
            />
          </Stack>
          {invalidRange && <Alert severity="error">시작일이 종료일보다 늦습니다.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy}>
          취소
        </Button>
        <LoadingButton
          variant="contained"
          loading={busy}
          disabled={!name.trim() || invalidRange}
          onClick={submit}
        >
          {editing ? "수정" : "만들기"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
